import type { ApplyRowResult, DriftRow } from "../../../api/companySlugDrift.service.js";

/**
 * Builds the rollback file for a Company Slug Drift run (SOFTSUPP-532).
 *
 * The apply endpoint overwrites office identity in place and keeps no history, so without
 * this file a run is irreversible — the only way back would be restoring a dump. The
 * response carries each row's pre-write `previous` value, captured inside the same
 * transaction that wrote it; this turns those into a CSV an operator can keep, and hand
 * back to the same tool to restore the earlier state.
 *
 * Only rows the run actually WROTE are included. A `stale`/`not_found`/`failed` row was
 * never written, so listing it would invite an operator to "restore" a value the database
 * never held.
 *
 * A `previous` of null means the column was empty before the run — the commonest shape in
 * the Company name category. It is emitted as an empty cell, and restore writes that back
 * as NULL. Dropping those rows would make exactly the rows people repair most one-way.
 */

export const ROLLBACK_HEADERS = ["officeId", "category", "proposed", "companyName"] as const;

const csvCell = (value: string | null | undefined): string => {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function buildRollbackCsv(
  results: ApplyRowResult[],
  scanned: DriftRow[],
): { csv: string; rowCount: number } {
  // companyName is carried purely so a human can recognise a row later; the tool itself
  // only needs officeId + category + proposed.
  const nameByOffice = new Map(scanned.map(r => [`${r.officeId}:${r.category}`, r.companyName]));

  const written = results.filter(r => r.status === "applied");
  const lines = [ROLLBACK_HEADERS.join(",")];

  for (const row of written) {
    lines.push(
      [
        csvCell(row.officeId),
        csvCell(row.category),
        // The rollback's TARGET is the run's previous value — that is the whole point.
        csvCell(row.previous),
        csvCell(nameByOffice.get(`${row.officeId}:${row.category}`)),
      ].join(","),
    );
  }

  return { csv: `${lines.join("\n")}\n`, rowCount: written.length };
}

/** Filename carries the timestamp so successive runs do not overwrite each other. */
export function rollbackFileName(now: Date): string {
  const stamp = now.toISOString().replace(/[:.]/g, "-").slice(0, 19);
  return `company-slug-drift-rollback-${stamp}.csv`;
}

/**
 * The PRE-apply rollback, built from the scan's `current` values.
 *
 * The post-apply file is authoritative — it carries what the database actually held at
 * the moment of the write. But it only exists once the response comes back, so a tab that
 * dies mid-run, a lost response, or a closed banner leaves the operator with no undo at
 * all. This file is available the moment rows are selected, which is when the operator
 * still has a choice.
 *
 * Rows the run ends up NOT writing (`stale`, `not_found`, `failed`) are covered here too.
 * Restoring one is harmless: it writes back the value the scan already saw, so it is a
 * noop unless something else changed the row in between — in which case the operator
 * genuinely wants to know.
 *
 * A row whose `current` is empty is INCLUDED, as an empty cell: restore writes that back
 * as NULL. Skipping them — which this originally did — silently produced a header-only
 * file for the Company name category, where an empty copy is the usual case.
 */
export function buildPlannedRollbackCsv(rows: DriftRow[]): { csv: string; rowCount: number } {
  const usable = rows;
  const lines = [ROLLBACK_HEADERS.join(",")];

  for (const row of usable) {
    lines.push(
      [
        csvCell(row.officeId),
        csvCell(row.category),
        csvCell(row.current),
        csvCell(row.companyName),
      ].join(","),
    );
  }

  return { csv: `${lines.join("\n")}\n`, rowCount: usable.length };
}
